import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HardDrives, Info } from '@phosphor-icons/react';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { nasApi } from './nasApi';
import { NAS_TABS, DEFAULT_TAB } from './nasConstants';
import { NAS_I18N } from './nasI18n';
import NasArchiveBrowse from './NasArchiveBrowse';
import NasArchiveSearch from './NasArchiveSearch';
import NasArchiveChanges from './NasArchiveChanges';
import NasArchiveStatus from './NasArchiveStatus';

// NAS Archive — independent page under DocsLayout at /documents/nas.
// Tabs are internal ?tab= state and never affect the DMS ?view= state.
export default function NasArchivePage() {
  const [meta, setMeta] = useState(null);
  const [roots, setRoots] = useState([]);
  const [error, setError] = useState('');
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || DEFAULT_TAB;

  const loadMeta = useCallback(async () => {
    try {
      const m = await nasApi.meta();
      setMeta(m);
      try { setRoots((await nasApi.roots()).roots || []); } catch { setRoots([]); }
    } catch (e) {
      setError(e?.response?.data?.detail || 'NASアーカイブを利用する権限がありません');
    }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const setTab = (t) => { const n = new URLSearchParams(params); n.set('tab', t); setParams(n, { replace: true }); };

  if (error) {
    return (
      <div className="max-w-[720px] py-16 text-center" data-testid="nas-archive-error">
        <Info size={26} className="mx-auto text-zinc-300 mb-2" />
        <p className="text-sm text-zinc-600">{error}</p>
      </div>
    );
  }
  if (!meta) return <p className="py-20 text-center text-sm text-zinc-400">読み込み中…</p>;

  const flagOn = (key) => !!meta.flags?.[key];
  const activeTab = NAS_TABS.find((t) => t.key === tab) || NAS_TABS[0];

  return (
    <div className="max-w-[1600px]" data-testid="nas-archive-page">
      <PageHeader
        title="NAS参照（AI検索対象）"
        eyebrow={<span className="inline-flex items-center gap-1.5 text-xs text-zinc-500"><HardDrives size={14} /> 読み取り専用 · NASの原本を閲覧・検索</span>}
        description="NAS上の書類を、そのままのフォルダー構成で閲覧・検索します（読み取り専用）。すべての書類（DMS）とは別の機能です。"
        testid="nas-page-header"
      />

      {/* tabs */}
      <div className="mt-4 border-b border-zinc-200 flex items-center gap-1" role="tablist" data-testid="nas-tabs">
        {NAS_TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
            onClick={() => setTab(t.key)} data-testid={t.testid}
            className={`px-3 min-h-[40px] text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-teal-700 text-teal-800' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5" role="tabpanel" data-testid={`nas-panel-${tab}`}>
        {/* Status tab is always reachable for managers so flags can be enabled. */}
        {activeTab.key === 'status' ? (
          <NasArchiveStatus meta={meta} roots={roots} onFlagsChanged={loadMeta} />
        ) : !flagOn(activeTab.flag) ? (
          <DisabledNotice canManage={meta.can_manage} />
        ) : activeTab.key === 'browse' ? (
          <NasArchiveBrowse meta={meta} roots={roots} />
        ) : activeTab.key === 'search' ? (
          <NasArchiveSearch meta={meta} roots={roots} />
        ) : (
          <NasArchiveChanges />
        )}
      </div>
    </div>
  );
}

function DisabledNotice({ canManage }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-6 text-center" data-testid="nas-tab-disabled">
      <Info size={22} className="mx-auto text-zinc-400 mb-2" />
      <p className="text-sm font-medium text-zinc-700">{NAS_I18N.disabled_title}</p>
      <p className="text-sm text-zinc-500 mt-1">
        {canManage ? NAS_I18N.disabled_hint : 'この機能はまだ利用できません。管理者にお問い合わせください。'}
      </p>
    </div>
  );
}
